import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../../contexts/AuthContext';
import { useTranslation } from 'react-i18next';
import Icon from '../../../components/AppIcon';
import AppImage from '../../../components/AppImage';
import LanguageSelector from '../../../components/LanguageSelector';
import ThemeToggle from '../../../components/ThemeToggle';

const Header = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { profile, signOut } = useAuth();
  const { t } = useTranslation();
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);

  const isActive = (path) => {
    return location?.pathname === path;
  };

  // Close sidebar on route change
  useEffect(() => {
    setIsSidebarOpen(false);
  }, [location?.pathname]);

  // Prevent body scroll when sidebar is open on mobile
  useEffect(() => {
    if (isSidebarOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isSidebarOpen]);

  const navItems = [
  {
    label: t('navigation.newRecommendation'),
    path: '/clinical-recommendation',
    icon: 'Plus',
    description: t('navigation.newRecommendationDesc')
  },
  {
    label: t('navigation.guidelines'),
    path: '/guidelines-reference',
    icon: 'BookOpen',
    description: t('navigation.guidelinesDesc')
  },
  {
    label: t('navigation.tutorials'),
    path: '/user-guide',
    icon: 'PlayCircle',
    description: t('navigation.tutorialsDesc')
  },
  {
    label: t('navigation.settings'),
    path: '/account-settings',
    icon: 'Settings',
    description: t('navigation.settingsDesc')
  }];


  const handleNavItemClick = (path) => {
    navigate(path);
    setIsSidebarOpen(false);
  };

  return (
    <>
      <header className="fixed top-0 left-0 right-0 z-50 bg-white/95 backdrop-blur-md border-b border-border shadow-sm">
        <div className="max-w-[1920px] mx-auto px-3 sm:px-4 md:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16 sm:h-18 md:h-20">
            {/* Left Section: Menu Button + Logo */}
            <div className="flex items-center gap-2 sm:gap-3 md:gap-4">
              {/* Hamburger Menu Button - Visible on Mobile/Tablet */}
              <button
                onClick={() => setIsSidebarOpen(!isSidebarOpen)}
                className="lg:hidden p-2 hover:bg-muted rounded-lg transition-colors touch-manipulation"
                aria-label="Toggle menu">

                <Icon
                  name={isSidebarOpen ? "X" : "Menu"}
                  size={24}
                  color="var(--color-foreground)" />

              </button>

              {/* Logo Section */}
              <div
                className="flex items-center gap-2 sm:gap-3 cursor-pointer group transition-all duration-200 touch-manipulation"
                onClick={() => navigate('/dashboard')}>

                <div className="relative">
                  <div className="w-9 h-9 sm:w-10 sm:h-10 md:w-12 md:h-12 rounded-xl overflow-hidden shadow-lg group-hover:shadow-xl transition-all duration-200 group-hover:scale-105 bg-white">
                    <AppImage
                      src="/assets/images/Screenshot_2025-12-22_at_12.13.42_AM-1766376908713.png"
                      alt="MY DOC REFER Logo"
                      className="w-full h-full object-contain" />

                  </div>
                  <div className="absolute -bottom-0.5 -right-0.5 w-2.5 h-2.5 sm:w-3 sm:h-3 bg-success rounded-full border-2 border-white"></div>
                </div>
                <div className="hidden sm:block">
                  <h1 className="text-base sm:text-lg md:text-xl font-bold bg-gradient-to-r from-primary to-accent bg-clip-text text-transparent">
                    {t('common.appName')}
                  </h1>
                  <p className="text-[9px] sm:text-[10px] text-muted-foreground font-medium tracking-wide uppercase">
                    {t('common.tagline')}
                  </p>
                </div>
              </div>
            </div>

            {/* Desktop Navigation - Hidden on Mobile/Tablet */}
            <nav className="hidden lg:flex items-center gap-2 xl:gap-3">
              {navItems?.map((item) => {
                const active = isActive(item?.path);
                return (
                  <button
                    key={item?.path}
                    onClick={() => navigate(item?.path)}
                    className={`
                      group relative px-3 xl:px-4 py-2
                      rounded-lg flex items-center gap-2.5
                      transition-all duration-200 ease-out
                      ${active ?
                    'bg-primary text-white shadow-md shadow-primary/25' :
                    'text-foreground hover:bg-muted/80'}
                    `
                    }>

                    {active &&
                    <div className="absolute inset-0 bg-gradient-to-r from-primary to-accent rounded-lg opacity-100"></div>
                    }
                    
                    <div className="relative z-10 flex items-center gap-2.5">
                      <Icon
                        name={item?.icon}
                        size={18}
                        color={active ? 'white' : 'currentColor'}
                        className={`transition-transform duration-200 ${active ? '' : 'group-hover:scale-110'}`} />

                      <div className="flex flex-col items-start">
                        <span className={`text-sm font-semibold ${active ? 'text-white' : ''}`}>
                          {item?.label}
                        </span>
                        <span className={`text-[10px] hidden xl:block ${active ? 'text-white/80' : 'text-muted-foreground'}`}>
                          {item?.description}
                        </span>
                      </div>
                    </div>

                    {!active &&
                    <div className="absolute inset-0 rounded-lg bg-gradient-to-r from-primary/5 to-accent/5 opacity-0 group-hover:opacity-100 transition-opacity duration-200"></div>
                    }
                  </button>);

              })}
            </nav>

            {/* Right Section */}
            <div className="flex items-center gap-2 sm:gap-3 md:gap-4">
              {/* Theme Toggle - Always visible */}
              <ThemeToggle compact />

              {/* Language Selector - Hidden on small mobile */}
              <div className="hidden sm:block">
                <LanguageSelector />
              </div>

              {/* Credit Display - Compact on mobile */}
              <div className="flex items-center gap-1.5 sm:gap-2 px-2 sm:px-3 md:px-4 py-1.5 sm:py-2 bg-gradient-to-r from-primary/10 to-accent/10 rounded-lg border border-primary/20 shadow-sm">
                <Icon name="Zap" size={16} color="var(--color-primary)" className="animate-pulse" />
                <div className="flex flex-col">
                  <span className="text-[10px] sm:text-xs text-muted-foreground font-medium hidden sm:block">
                    {t('userMenu.credits')}
                  </span>
                  <span className="text-xs sm:text-sm font-bold text-foreground">
                    {profile?.credits_remaining || 0}
                  </span>
                </div>
              </div>
              
              {/* User Menu with visible username */}
              <div className="relative">
                <button
                  onClick={() => setIsUserMenuOpen(!isUserMenuOpen)}
                  className="flex items-center gap-2 sm:gap-3 px-2 sm:px-3 py-2 hover:bg-muted rounded-lg transition-all duration-200 group touch-manipulation"
                >
                  <div className="relative">
                    <div className="w-8 h-8 sm:w-9 sm:h-9 bg-gradient-to-br from-primary to-accent text-white rounded-lg flex items-center justify-center font-bold text-xs sm:text-sm shadow-md group-hover:shadow-lg transition-all duration-200 group-hover:scale-105">
                      {profile?.full_name?.charAt(0) || 'U'}
                    </div>
                    <div className="absolute -bottom-0.5 -right-0.5 w-2 h-2 sm:w-2.5 sm:h-2.5 bg-success rounded-full border-2 border-white"></div>
                  </div>
                  {/* Username visible on all devices */}
                  <div className="flex flex-col items-start min-w-0">
                    <span className="text-xs sm:text-sm font-semibold text-foreground truncate max-w-[80px] sm:max-w-[120px] md:max-w-[140px]">
                      {profile?.full_name || 'User'}
                    </span>
                    <span className="hidden sm:block text-[10px] text-muted-foreground">
                      {profile?.subscription_tier || 'Free'}
                    </span>
                  </div>
                  <Icon
                    name={isUserMenuOpen ? "ChevronUp" : "ChevronDown"}
                    size={16}
                    color="var(--color-muted-foreground)"
                    className="hidden sm:block transition-transform duration-200"
                  />
                </button>
                
                {/* Dropdown Menu */}
                {isUserMenuOpen &&
                <>
                    <div
                    className="fixed inset-0 z-40"
                    onClick={() => setIsUserMenuOpen(false)}>
                  </div>
                    <div className="absolute right-0 mt-2 w-64 bg-white rounded-xl shadow-xl border border-border z-50 overflow-hidden animate-in fade-in slide-in-from-top-2 duration-200">
                      {/* User Info */}
                      <div className="p-4 bg-gradient-to-br from-primary/5 to-accent/5 border-b border-border">
                        <div className="flex items-center gap-3">
                          <div className="w-12 h-12 bg-gradient-to-br from-primary to-accent text-white rounded-lg flex items-center justify-center font-bold shadow-md">
                            {profile?.full_name?.charAt(0) || 'U'}
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-semibold text-foreground truncate">
                              {profile?.full_name || 'User'}
                            </p>
                            <p className="text-xs text-muted-foreground truncate">
                              {profile?.email || ''}
                            </p>
                            <div className="mt-1 inline-flex items-center gap-1 px-2 py-0.5 bg-primary/10 rounded-full">
                              <Icon name="Crown" size={10} color="var(--color-primary)" />
                              <span className="text-[10px] font-medium text-primary">
                                {profile?.subscription_tier || 'Free'}
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>
                      
                      {/* Menu Items */}
                      <div className="p-2">
                        <button
                        onClick={() => {
                          navigate('/account-settings');
                          setIsUserMenuOpen(false);
                        }}
                        className="w-full text-left px-3 py-2.5 text-sm hover:bg-muted rounded-lg transition-colors flex items-center gap-3 group touch-manipulation">

                          <Icon name="Settings" size={16} className="text-muted-foreground group-hover:text-foreground transition-colors" />
                          <span className="font-medium">{t('userMenu.accountSettings')}</span>
                        </button>
                        <button
                        onClick={() => {
                          navigate('/purchase-credits');
                          setIsUserMenuOpen(false);
                        }}
                        className="w-full text-left px-3 py-2.5 text-sm hover:bg-muted rounded-lg transition-colors flex items-center gap-3 group touch-manipulation">

                          <Icon name="CreditCard" size={16} className="text-muted-foreground group-hover:text-foreground transition-colors" />
                          <span className="font-medium">{t('userMenu.purchaseCredits')}</span>
                        </button>
                        <div className="my-2 border-t border-border"></div>
                        <button
                        onClick={() => {
                          signOut();
                          setIsUserMenuOpen(false);
                        }}
                        className="w-full text-left px-3 py-2.5 text-sm text-destructive hover:bg-destructive/10 rounded-lg transition-colors flex items-center gap-3 group touch-manipulation">

                          <Icon name="LogOut" size={16} />
                          <span className="font-medium">{t('userMenu.signOut')}</span>
                        </button>
                      </div>
                    </div>
                  </>
                }
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* Mobile/Tablet Sidebar */}
      <>
        {/* Backdrop Overlay */}
        {isSidebarOpen &&
        <div
          className="fixed inset-0 bg-black/50 backdrop-blur-sm z-40 lg:hidden transition-opacity duration-300"
          onClick={() => setIsSidebarOpen(false)}>
        </div>
        }

        {/* Sidebar */}
        <aside
          className={`
            fixed top-16 left-0 bottom-0 w-72 bg-white shadow-2xl z-40 
            transform transition-transform duration-300 ease-out lg:hidden
            ${isSidebarOpen ? 'translate-x-0' : '-translate-x-full'}
          `}>

          <div className="h-full flex flex-col overflow-y-auto">
            {/* Sidebar Header */}
            <div className="p-4 border-b border-border bg-gradient-to-br from-primary/5 to-accent/5">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-gradient-to-br from-primary to-accent text-white rounded-xl flex items-center justify-center font-bold shadow-md">
                  {profile?.full_name?.charAt(0) || 'U'}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-foreground truncate">
                    {profile?.full_name || 'User'}
                  </p>
                  <p className="text-xs text-muted-foreground truncate">
                    {profile?.email || ''}
                  </p>
                </div>
              </div>
            </div>

            {/* Navigation Items */}
            <nav className="flex-1 p-4 space-y-2">
              {navItems?.map((item) => {
                const active = isActive(item?.path);
                return (
                  <button
                    key={item?.path}
                    onClick={() => handleNavItemClick(item?.path)}
                    className={`
                      w-full text-left px-4 py-3 rounded-xl
                      flex items-center gap-3
                      transition-all duration-200 touch-manipulation
                      ${active ?
                    'bg-gradient-to-r from-primary to-accent text-white shadow-lg shadow-primary/25' :
                    'text-foreground hover:bg-muted'}
                    `
                    }>

                    <Icon
                      name={item?.icon}
                      size={20}
                      color={active ? 'white' : 'currentColor'} />

                    <div className="flex-1">
                      <div className={`text-sm font-semibold ${active ? 'text-white' : ''}`}>
                        {item?.label}
                      </div>
                      <div className={`text-xs ${active ? 'text-white/80' : 'text-muted-foreground'} mt-0.5`}>
                        {item?.description}
                      </div>
                    </div>
                    {active &&
                    <Icon name="ChevronRight" size={16} color="white" />
                    }
                  </button>);

              })}
            </nav>

            {/* Sidebar Footer */}
            <div className="p-4 border-t border-border space-y-3">
              {/* Theme Toggle for Mobile Sidebar */}
              <div className="flex items-center justify-between px-1 mb-3">
                <span className="text-xs font-medium text-muted-foreground">
                  {t('settings.theme')}
                </span>
                <ThemeToggle />
              </div>

              {/* Language Selector for Mobile */}
              <div className="sm:hidden">
                <div className="text-xs font-medium text-muted-foreground mb-2 px-1">
                  {t('common.language')}
                </div>
                <LanguageSelector />
              </div>

              {/* Quick Actions */}
              <button
                onClick={() => {
                  navigate('/purchase-credits');
                  setIsSidebarOpen(false);
                }}
                className="w-full px-4 py-3 bg-gradient-to-r from-primary/10 to-accent/10 hover:from-primary/20 hover:to-accent/20 rounded-xl border border-primary/20 flex items-center gap-3 transition-all duration-200 touch-manipulation">

                <Icon name="Zap" size={18} color="var(--color-primary)" />
                <div className="flex-1 text-left">
                  <div className="text-sm font-semibold text-foreground">
                    {t('userMenu.purchaseCredits')}
                  </div>
                  <div className="text-xs text-muted-foreground">
                    {profile?.credits_remaining || 0} {t('userMenu.credits')}
                  </div>
                </div>
                <Icon name="ChevronRight" size={16} color="var(--color-muted-foreground)" />
              </button>

              <button
                onClick={() => {
                  signOut();
                  setIsSidebarOpen(false);
                }}
                className="w-full px-4 py-3 bg-destructive/10 hover:bg-destructive/20 rounded-xl flex items-center gap-3 transition-all duration-200 touch-manipulation">

                <Icon name="LogOut" size={18} color="var(--color-destructive)" />
                <span className="flex-1 text-left text-sm font-semibold text-destructive">
                  {t('userMenu.signOut')}
                </span>
              </button>
            </div>
          </div>
        </aside>
      </>
    </>);

};

export default Header;